import React, { useEffect, useState } from 'react';
import { X, AlertCircle, ArrowUpDown, User } from 'lucide-react';
import { getPenyesuaianStok, createPenyesuaianStok, deletePenyesuaianStok, getInventoriSpareparts, getGudang } from '../../api';
import { useAuth } from '../../AuthContext';
import SearchableSelect from '../../components/SearchableSelect';

export default function PenyesuaianStok() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [spareparts, setSpareparts] = useState([]);
  const [gudangList, setGudangList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    tanggal: new Date().toISOString().slice(0, 10), sparepart_id: '', gudang_id: '',
    jenis: 'Masuk', qty: 1, harga_satuan: 0, alasan: '', keterangan: ''
  });

  const load = () => {
    setLoading(true);
    Promise.all([getPenyesuaianStok(), getInventoriSpareparts({}), getGudang()])
      .then(([res, spRes, gRes]) => { setData(res.data); setSpareparts(spRes.data || []); setGudangList(gRes.data || []); })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stok yang ditampilkan mengikuti gudang terpilih (model stok per gudang)
  const effectiveStock = (p) => {
    const gid = Number(form.gudang_id) || 0;
    const pg = p.stok_per_gudang;
    if (pg && Object.keys(pg).length > 0) {
      if (!gid) return 0;
      return Number(pg[String(gid)]) || 0;
    }
    const home = Number(p.gudang_id) || 0;
    if (home > 0 && gid > 0 && home !== gid) return 0;
    return Number(p.stock) || 0;
  };
  const sparepartsForGudang = spareparts.map(p => ({ ...p, stock: effectiveStock(p) }));
  const chosenPart = form.sparepart_id ? spareparts.find(x => String(x.id) === String(form.sparepart_id)) : null;
  const chosenAvail = chosenPart ? effectiveStock(chosenPart) : null;

  const handleSave = async () => {
    setError('');
    if (!form.sparepart_id) { setError('Pilih sparepart'); return; }
    if (!form.alasan) { setError('Alasan penyesuaian harus diisi'); return; }
    // Validasi stok per gudang (khusus jenis Keluar)
    if (form.jenis === 'Keluar' && chosenPart && chosenAvail !== null) {
      if (chosenAvail <= 0) { setError(`"${chosenPart.name}" tidak tersedia di gudang terpilih (stok 0).`); return; }
      if (Number(form.qty) > chosenAvail) { setError(`Stok "${chosenPart.name}" di gudang ini tidak mencukupi: tersedia ${chosenAvail}, diminta ${form.qty}.`); return; }
    }
    setSaving(true);
    try {
      await createPenyesuaianStok({
        tanggal: form.tanggal, sparepart_id: Number(form.sparepart_id),
        gudang_id: parseInt(form.gudang_id) || 0, jenis: form.jenis,
        qty: Number(form.qty), harga_satuan: Number(form.harga_satuan),
        alasan: form.alasan, keterangan: form.keterangan,
      });
      setShowModal(false); load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus penyesuaian ini?')) return;
    try { await deletePenyesuaianStok(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Penyesuaian Stok</h2>
            <p className="page-desc">{data.length} penyesuaian</p>
          </div>
          {hasMenuAction('inventori', 'create') && (
            <button className="btn btn-primary" onClick={() => { setError(''); setShowModal(!showModal); }}>
              <ArrowUpDown size={16} /><span>{showModal ? 'Tutup Form' : 'Buat Penyesuaian'}</span>
            </button>
          )}
        </div>
      </div>

      {showModal && (
        <div style={{
          marginTop: 16, padding: '20px 22px', borderRadius: 12,
          background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 2px 14px rgba(15,23,42,0.05)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ArrowUpDown size={18} style={{ color: 'var(--primary, #4f46e5)' }} />
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>Buat Penyesuaian Stok</h3>
            </div>
            <button className="btn-icon" onClick={() => setShowModal(false)} title="Tutup form"><X size={18} /></button>
          </div>
          {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
          <div className="form-grid">
            <div className="form-group"><label>Tanggal *</label><input type="date" value={form.tanggal} onChange={e => setForm({...form, tanggal: e.target.value})} /></div>
            <div className="form-group"><label>Jenis Penyesuaian *</label>
              <select value={form.jenis} onChange={e => setForm({...form, jenis: e.target.value})}>
                <option value="Masuk">Masuk (Tambah Stok)</option>
                <option value="Keluar">Keluar (Kurangi Stok)</option>
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Sparepart *</label>
              <SearchableSelect
                options={sparepartsForGudang}
                value={form.sparepart_id}
                onChange={v => setForm({...form, sparepart_id: v})}
                placeholder="Ketik kode / nama item..."
              />
              {chosenPart && chosenAvail !== null && (
                <div style={{ fontSize: '0.72rem', color: chosenAvail <= 0 ? '#f5365c' : '#2dce89', marginTop: 4 }}>
                  {chosenAvail <= 0
                    ? `⚠ "${chosenPart.name}" stok 0 di gudang terpilih — tidak bisa penyesuaian Keluar dari gudang ini.`
                    : `Stok "${chosenPart.name}" di gudang ini: ${chosenAvail}`}
                </div>
              )}
            </div>
            <div className="form-group"><label>Gudang</label>
              <select value={form.gudang_id} onChange={e => setForm({...form, gudang_id: e.target.value})}>
                <option value="">-- Pilih Gudang --</option>
                {gudangList.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
              </select>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>Stok item mengikuti gudang terpilih — item yang tidak berada di gudang ini tampil <b>stok 0</b>.</div>
            </div>
            <div className="form-group"><label>Qty *</label><input type="number" value={form.qty} onChange={e => setForm({...form, qty: e.target.value})} min="1" placeholder="0" /></div>
            <div className="form-group"><label>Harga Satuan</label><input type="number" value={form.harga_satuan} onChange={e => setForm({...form, harga_satuan: e.target.value})} placeholder="0" /></div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Alasan *</label>
              <input type="text" value={form.alasan} onChange={e => setForm({...form, alasan: e.target.value})} placeholder="Contoh: Rusak, Hilang, Koreksi stok awal" />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Keterangan</label>
              <textarea rows={2} value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} placeholder="Keterangan tambahan" />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Menyimpan...' : <><ArrowUpDown size={16} /> Simpan Penyesuaian</>}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No. Ref</th><th>Tanggal</th><th>Sparepart</th><th>Gudang</th>
                <th>Jenis</th><th>Qty</th><th>Harga</th><th>Alasan</th><th>Dibuat Oleh</th><th style={{ width: 60 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.map(d => (
                <tr key={d.id}>
                  <td><strong style={{ fontSize: '0.8rem' }}>{d.no_ref}</strong></td>
                  <td>{d.tanggal}</td>
                  <td><code>{d.code}</code> {d.name}</td>
                  <td>{d.gudang_nama || '-'}</td>
                  <td>
                    <span className={`badge ${d.jenis === 'Masuk' ? 'badge-success' : 'badge-danger'}`}>
                      {d.jenis === 'Masuk' ? '+ Masuk' : '- Keluar'}
                    </span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{d.qty}</td>
                  <td>{rupiah(d.harga_satuan)}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--gray-500)', maxWidth: 200 }}>{d.alasan}</td>
                  <td><span className="cell-with-icon"><User size={12} className="text-muted" />{d.created_by_name || '-'}</span></td>
                  <td>
                    {hasMenuAction('inventori', 'delete') && (
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(d.id)}><X size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={10} className="text-center text-muted" style={{ padding: 40 }}>Belum ada penyesuaian stok</td></tr>}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
